import { useMemo } from "react";
import { useSelector } from "react-redux";

import { emailInRoles } from "../constants/selectOptions/selectOptionsLoanRoles";

// Hoisted to module scope so useMemo dependencies stay stable and eslint
// doesn't flag the array as a missing dependency on every render.
const ALLOW_DEV_FEATURES_EMAILS = [
  "holden.hargrave@Lendmarq.com",
  "dustin@Lendmarq.com",
  "dustin@lendmarq.com",
  "danny@arrowservicing.com",
  "jordan@resfinpartners.com",
  "shaun@resilender.com",
  "opsmgmt@resilender.com",
  "michelle.hamblen@arrowpointemgmt.com",
  "shyrose@arrowservicing.com", // Shyrose
  "shyrose@arrowpointemgmt.com", // Shyrose
  "dev2@arrowservicing.com", // Chris
  "dev1@arrowservicing.com", // Me
  "sam@lendmarq.com",
];

// Users explicitly allowed to access the Quick Application screens
// (/quick-app and /quick-applications). The nav item was removed for
// everyone; these screens stay reachable by direct URL only for this
// allowlist. Everyone else is redirected to the standard loan-application
// screens. Email comparison is case-insensitive.
const ALLOW_QUICK_APPLICATIONS_EMAILS = [
  "dev1@arrowservicing.com", // Me
  "dev2@arrowservicing.com", // Chris
  "shyrose@arrowservicing.com", // Shyrose
  "shyrose@arrowpointemgmt.com", // Shyrose
];

// Super admins — the small internal group with access to platform-level
// surfaces (the Users tab, the Lock Queue). This is an explicit email
// allowlist rather than a backend permission because there is no
// `super_admin` permission on the user record yet. Email comparison is
// case-insensitive.
const SUPER_ADMIN_EMAILS = [
  "dev1@arrowservicing.com", // Christopher Wolpert
  "dev2@arrowservicing.com", // Jimmy Oliva
  "shyrose@arrowservicing.com", // Shyrose
  "shyrose@arrowpointemgmt.com", // Shyrose
  "jordan@resfinpartners.com",
  "holden.hargrave@lendmarq.com",
  "ethan.lichtblau@arrowpointemgmt.com", // Ethan Lichtblau
];

// The board's developers — the only people shown the Request Board's auth-token
// button (their sign-in token, for the /request-board-* Claude Code commands).
// Deliberately an explicit list, NOT widened by super admin (owner 2026-10-22).
const REQUEST_BOARD_TOKEN_EMAILS = [
  "dev1@arrowservicing.com", // Christopher Wolpert
  "dev2@arrowservicing.com", // Jimmy Oliva
  "holden.hargrave@lendmarq.com", // Holden Hargrave
];

// Extra emails granted the Users tab without being full super admins. Super
// admins get it implicitly — this list is additive on top of them.
const ALLOW_USERS_TAB_EMAILS = ["zac.miller@lendmarq.com"];

// Capital markets desk — the only group allowed to confirm / return / manage a
// rate lock (rate-lock confirmation + pricing). Backend tags these users with the
// `capital_markets` team; until that propagates everywhere, this email allowlist
// is the source of truth. Email comparison is case-insensitive.
const CAPITAL_MARKETS_EMAILS = [
  "larbi@resfinpartners.com",
  "kate@resfinpartners.com",
  "dev1@arrowservicing.com", // Christopher Wolpert
  "dev2@arrowservicing.com", // Jimmy Oliva
  "dvora@resfinpartners.com",
  "jordan@resfinpartners.com",
  "shyrose@arrowpointemgmt.com",
  "holden.hargrave@lendmarq.com",
];

// Closing desk — mirrors the backend's `assignment_options.CLOSERS` list (feeds
// the loan-level `loanCloser` dropdown). Until a `closing` team tag propagates,
// this email allowlist is the source of truth. Email comparison is case-insensitive.
const CLOSERS_EMAILS = [
  "christina.baucom@arrowpointemgmt.com",
  "marcie@arrowpointemgmt.com",
  "rebecca.rietta@arrowpointemgmt.com",
  "shyrose@arrowpointemgmt.com",
];

// Users whose email matches one of these domains have Borrower Portal
// features hidden — the "Borrower Portal Users" nav item, the screen itself,
// and the "Invite to Portal" action from the loan drilldown's Applicants tab.
// Extend this list when additional orgs need the same restriction.
const BORROWER_PORTAL_RESTRICTED_EMAIL_DOMAINS = [
  "maratonacapital.com",
  // "arrowservicing.com",
  //
];

const lcList = (list) => list.map((e) => e.toLowerCase());
const SUPER_ADMIN_LC = lcList(SUPER_ADMIN_EMAILS);
const USERS_TAB_LC = lcList(ALLOW_USERS_TAB_EMAILS);
const CAPITAL_MARKETS_LC = lcList(CAPITAL_MARKETS_EMAILS);
const CLOSERS_LC = lcList(CLOSERS_EMAILS);
const DEV_FEATURES_LC = lcList(ALLOW_DEV_FEATURES_EMAILS);
const REQUEST_BOARD_TOKEN_LC = lcList(REQUEST_BOARD_TOKEN_EMAILS);
const QUICK_APPS_LC = lcList(ALLOW_QUICK_APPLICATIONS_EMAILS);
const BORROWER_PORTAL_BLOCKED_LC = lcList(BORROWER_PORTAL_RESTRICTED_EMAIL_DOMAINS);

const asList = (v) => (Array.isArray(v) ? v : []);
const hasAny = (list, values) => values.some((v) => asList(list).includes(v));

/**
 * Every permission flag for ONE user record, as a pure function — the hook
 * below runs it on the signed-in user; the drilldown Admin tab runs it on every
 * user from GET /users/all_users to list who passes each gate. One definition,
 * so the two can never disagree.
 */
export const resolvePermissions = (user) => {
  const rawEmail = user?.emailAddress;
  const email = rawEmail?.toLowerCase();
  const permissions = asList(user?.permissions);
  const roles = asList(user?.roles);
  const teams = asList(user?.teams);

  const isDevUser =
    rawEmail === "joliva@cliffcomortgage.com" ||
    rawEmail === "dev1@cliffcomortgage.com" ||
    rawEmail === "cwolpert@cliffcomortgage.com";

  const isTempDevUser =
    rawEmail === "joliva@cliffcomortgage.com" ||
    rawEmail === "dev1@cliffcomortgage.com" ||
    rawEmail === "cnguyen@cliffcomortgage.com" ||
    rawEmail === "cwolpert@cliffcomortgage.com";

  // Super admin: explicit email allowlist above. Gates platform-level
  // surfaces (Users tab, Lock Queue). Computed before isAdmin so that any
  // admin check also grants super admins implicitly.
  const isSuperAdmin = !!email && SUPER_ADMIN_LC.includes(email);

  // Admin: carries the `admin` permission OR is a super admin. Super admins
  // are always treated as admins, so every admin-gated surface includes them.
  const isAdmin = permissions.includes("admin") || isSuperAdmin;

  const isUnderwriter = permissions.includes("underwriting");

  // Users tab: super admins plus the extra per-email grants above.
  const canAccessUsersTab =
    isSuperAdmin || (!!email && USERS_TAB_LC.includes(email));

  // Capital-markets desk: email is in the allowlist OR the user carries the
  // `capital_markets` team tag. Gates rate-lock confirm / return / manage.
  const isCapitalMarkets =
    (!!email && CAPITAL_MARKETS_LC.includes(email)) ||
    teams.includes("capital_markets");

  // Closing desk: email is in the allowlist OR the user carries a `closing`
  // team tag (parallels `isCapitalMarkets`).
  const isCloser =
    (!!email && CLOSERS_LC.includes(email)) || teams.includes("closing");

  // Closing desk, broad: the flag above OR the EV Closers loan-role roster
  // (what the loanCloser dropdown is built from).
  const isCloserAny = isCloser || emailInRoles(rawEmail, ["closers"]);

  // Sales / loan officers: the `sales` / `sales_admin` permission, or the
  // `loan_officer` / `sales_admin` role. Note the ported `roles` were
  // mass-defaulted to loan_officer on the backend, so this is broad.
  const isSalesOrLoanOfficer =
    hasAny(permissions, ["sales", "sales_admin"]) ||
    hasAny(roles, ["loan_officer", "sales_admin"]);

  // Processing: the `processor` role / team, a processing permission, or the
  // EV Processor loan-role roster.
  const isProcessor =
    hasAny(roles, ["processor"]) ||
    hasAny(teams, ["processor", "processing"]) ||
    hasAny(permissions, ["processing", "processor"]) ||
    emailInRoles(rawEmail, ["processor"]);

  // Loan Lock tab (view + request): underwriting, admin, capital markets,
  // sales / loan officers, processing and closers. Confirm / return / cancel stay capital
  // markets (backend-enforced).
  const canViewLoanLock =
    isUnderwriter ||
    isAdmin ||
    isCapitalMarkets ||
    isSalesOrLoanOfficer ||
    isProcessor ||
    isCloserAny;

  // Gate for in-progress / dev-only features — email allowlist only.
  // Case-insensitive since the allowlist mixes casing.
  const canAccessDevFeatures = !!email && DEV_FEATURES_LC.includes(email);

  // Request Board → auth-token button: the three board developers only.
  const canSeeRequestBoardToken = !!email && REQUEST_BOARD_TOKEN_LC.includes(email);

  // Borrower Portal access gate. False if the user's email domain is on the
  // restricted-domains list, true otherwise (case-insensitive).
  const domain = email?.split("@")[1];
  const canAccessBorrowerPortal =
    !email || !domain || !BORROWER_PORTAL_BLOCKED_LC.includes(domain);

  // Quick Application access gate — the explicit email allowlist above; the
  // nav item was removed for everyone, so these screens are direct-URL only.
  const canAccessQuickApplications = !!email && QUICK_APPS_LC.includes(email);

  return {
    isTempDevUser,
    isDevUser,
    isUnderwriter,
    isAdmin,
    isSuperAdmin,
    isCapitalMarkets,
    isCloser,
    isCloserAny,
    isSalesOrLoanOfficer,
    isProcessor,
    canViewLoanLock,
    canAccessUsersTab,
    canAccessDevFeatures,
    canSeeRequestBoardToken,
    canAccessBorrowerPortal,
    canAccessQuickApplications,
  };
};

export const usePermissionsHook = () => {
  const user = useSelector((state) => state.user);
  return useMemo(() => resolvePermissions(user), [user]);
};

// const uw = {
//   _id: "687ac3545eed4b631ed32db4",
//   createdDate: "2025-07-18T17:57:40.566000",
//   emailAddress: "bhavani@arrowservicing.com",
//   firstName: "Bhavani",
//   fullName: "Bhavani Vallapu",
//   lastName: "Vallapu",
//   member_id: "member-live-763eef6c-fff1-4629-87fd-691e62632a83",
//   organization_id: "organization-live-e345fd6c-502a-44a5-ab4c-cf836d816e10",
//   permissions: ["admin"], //"sales" , "underwriting", "admin"
//   phone_number: "None",
//   roles: ["loan_officer"],
//   teams: [],
//   trusted_metadata: {
//     emailAddress: "bhavani@arrowservicing.com",
//     firstName: "Bhavani",
//     lastName: "Vallapu",
//   },
//   user_project_type: "b2b",
// };

// const lo = {
//   _id: "687ac3545eed4b631ed32db4",
//   createdDate: "2025-07-18T17:57:40.566000",
//   emailAddress: "bhavani@arrowservicing.com",
//   firstName: "Bhavani",
//   fullName: "Bhavani Vallapu",
//   lastName: "Vallapu",
//   member_id: "member-live-763eef6c-fff1-4629-87fd-691e62632a83",
//   organization_id: "organization-live-e345fd6c-502a-44a5-ab4c-cf836d816e10",
//   permissions: ["admin"],
//   phone_number: "None",
//   roles: ["loan_officer"],
//   teams: [],
//   trusted_metadata: {
//     emailAddress: "bhavani@arrowservicing.com",
//     firstName: "Bhavani",
//     lastName: "Vallapu",
//   },
//   user_project_type: "b2b",
// };

// const dustin = {
//   _id: "687ac35c5eed4b631ed32dbd",
//   createdDate: "2025-07-18T17:57:48.568000",
//   emailAddress: "dustin@lendmarq.com",
//   firstName: "Dustin",
//   fullName: "Dustin Lauer",
//   lastName: "Lauer",
//   member_id: "member-live-81458c58-238e-4132-9dd7-b49054621000",
//   organization_id: "organization-live-e345fd6c-502a-44a5-ab4c-cf836d816e10",
//   permissions: ["sales_admin"],
//   phone_number: "None",
//   preferences: {},
//   roles: ["loan_officer"],
//   teams: [],
//   trusted_metadata: {
//     emailAddress: "dustin@lendmarq.com",
//     firstName: "Dustin",
//     lastName: "Lauer",
//   },
//   user_project_type: "b2b",
// };

// const jim = {
//   _id: "69209d06e28939fa8dfffaa0",
//   company: "Lendmarq",
//   createdDate: "2025-11-21T12:10:30.734000",
//   emailAddress: "dev2@arrowservicing.com",
//   emailSignature: " ",
//   firstName: "Jimmy",
//   fullName: "Jimmy Oliva",
//   lastName: "Oliva",
//   member_id: "member-live-1b0b5c95-55b7-47d5-82e7-385a6b3bfb92",
//   organization_id: "organization-live-e345fd6c-502a-44a5-ab4c-cf836d816e10",
//   permissions: ["admin"],
//   phone: "6314563373",
//   phone_number: "None",
//   preferences: {},
//   roles: ["admin"],
//   saved: {},
//   teams: [],
//   testData: { testNumber: 3 },
//   trusted_metadata: {
//     emailAddress: "dev2@arrowservicing.com",
//     firstName: "Jimmy",
//     lastName: "Oliva",
//   },
//   user_project_type: "b2b",
// };
